import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { usePlots } from "../context/PlotContext";
import { fetchIssues, fetchServiceTickets, fetchInvoices, fetchMaintenanceTypes } from "../api/api";
import { FaRegCalendarAlt, FaHammer, FaRegCreditCard, FaExclamationTriangle, FaTools, FaGift } from "react-icons/fa";

// Same set of shortcuts as the mobile app's MyFarmScreen QUICK ACTIONS grid,
// pointing at the equivalent web routes — icons match the mobile app's
// Ionicons choices (vector icons instead of emoji, for a consistent look).
// White here (not the mobile app's per-icon colors) since these render on a
// solid green button, not the mobile app's light pastel tile background.
const QUICK_ACTIONS = [
  { icon: FaRegCalendarAlt, label: "Book a Visit", path: "/customervisits" },
  { icon: FaHammer, label: "Farm Maintenance", path: "/farm-maintenance" },
  // My Documents hidden for the first launch — re-enable post-launch by
  // uncommenting; the /documents route itself is untouched.
  // { icon: FaRegFileAlt, label: "My Documents", path: "/documents" },
  { icon: FaRegCreditCard, label: "Billing", path: "/billing" },
  { icon: FaExclamationTriangle, label: "Report Issue", path: "/customer-issues" },
  { icon: FaTools, label: "Service Request", path: "/customer-service-tickets" },
  { icon: FaGift, label: "Refer a Friend", path: "/referral" },
];

function errorMessage(err) {
  return err.response?.data?.detail || "Could not load your farm details. Please try again.";
}

// Types that don't carry a maintenance subscription at all — the validity
// date is meaningless for them, so the status is always "Not Applicable"
// regardless of what maintenance_validity_date happens to hold.
const MAINTENANCE_EXEMPT_TYPES = ["own maintenance", "no maintenance"];

function maintenanceStatusFor(typeName, validityDate) {
  if (typeName && MAINTENANCE_EXEMPT_TYPES.includes(typeName.trim().toLowerCase())) {
    return "Not Applicable";
  }
  if (!validityDate) return "-";
  const validity = new Date(validityDate);
  validity.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return validity >= today ? "Active" : "Expired";
}

function MyFarm() {

  const { user } = useAuth();
  const navigate = useNavigate();
  const { plots, selectedPlot: plot, selectedPlotId, selectPlot, loading: plotsLoading } = usePlots();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openIssuesCount, setOpenIssuesCount] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [pendingBillsCount, setPendingBillsCount] = useState(0);
  const [requestPickerOpen, setRequestPickerOpen] = useState(false);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [issuesData, ticketsData, invoicesData, maintenanceTypesData] = await Promise.all([
          fetchIssues().catch(() => []),
          fetchServiceTickets().catch(() => []),
          fetchInvoices().catch(() => []),
          fetchMaintenanceTypes().catch(() => []),
        ]);
        const isOpen = (r) => r.status === "received" || r.status === "review";
        setOpenIssuesCount(issuesData.filter(isOpen).length);
        setOpenTicketsCount(ticketsData.filter(isOpen).length);
        setPendingBillsCount(invoicesData.filter((inv) => inv.status !== "paid").length);
        setMaintenanceTypes(maintenanceTypesData);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const maintenanceTypeName = maintenanceTypes.find((t) => t.id === plot?.maintenance_type_id)?.name
    || plot?.maintenance_type
    || null;
  const maintenanceStatus = maintenanceStatusFor(maintenanceTypeName, plot?.maintenance_validity_date);

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="myfarm-container">

          <h2 className="welcome-title">
            Welcome, {user?.full_name || "…"}
          </h2>

          {(loading || plotsLoading) && <p>Loading your farm details…</p>}
          {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

          {!loading && !plotsLoading && !error && !plot && (
            <p>No plot is registered to your account yet.</p>
          )}

          {!loading && !plotsLoading && !error && plot && (
            <>

              <div className="farm-card">

                {plots.length > 1 ? (
                  <select
                    value={selectedPlotId || ""}
                    onChange={(e) => selectPlot(Number(e.target.value))}
                    style={{ fontSize: "16px", fontWeight: 700, marginBottom: "10px", padding: "6px 10px", borderRadius: "8px", border: "1px solid #ddd" }}
                  >
                    {plots.map((p) => (
                      <option key={p.id} value={p.id}>📍 Farm Land {p.plot_number} - {p.location}</option>
                    ))}
                  </select>
                ) : (
                  <h3>📍 Farm Land {plot.plot_number} - {plot.location}</h3>
                )}

                <div className="farm-row">
                  <span>Area</span>
                  <strong>{plot.area_sqft} Sqft</strong>
                </div>

                <div className="farm-row">
                  <span>Registered On</span>
                  <strong>{new Date(plot.created_at).toLocaleDateString()}</strong>
                </div>

                <div className="farm-row">
                  <span>Maintenance Type</span>
                  <strong>{maintenanceTypeName || "-"}</strong>
                </div>

                <div className="farm-row">
                  <span>Water Supply</span>
                  <strong>{plot.water_supply || "-"}</strong>
                </div>

                {plot.co_owner_names?.length > 0 && (
                  <div className="farm-row">
                    <span>Co-owners</span>
                    <strong>{plot.co_owner_names.join(", ")}</strong>
                  </div>
                )}

                <hr />

                <div className="farm-row">
                  <span>Status</span>
                  <span
                    className={maintenanceStatus === "Active" ? "status-badge green" : "status-badge"}
                    style={maintenanceStatus === "Expired" ? { color: "#dc2626" } : undefined}
                  >
                    {maintenanceStatus}
                  </span>
                </div>

              </div>

              <div className="stats-grid" style={{ position: "relative" }}>

                <div
                  className="stats-card"
                  style={{ cursor: "pointer" }}
                  onClick={() => setRequestPickerOpen((prev) => !prev)}
                >
                  <h3>Open Requests</h3>
                  <h1 style={{ color: openIssuesCount + openTicketsCount > 0 ? "orange" : undefined }}>
                    {openIssuesCount + openTicketsCount}
                  </h1>
                </div>

                <div
                  className="stats-card"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/billing")}
                >
                  <h3>Pending Bills</h3>
                  <h1 style={{ color: pendingBillsCount > 0 ? "red" : undefined }}>
                    {pendingBillsCount}
                  </h1>
                </div>

                {requestPickerOpen && (
                  <div
                    style={{
                      position: "absolute", top: "100%", left: 0, marginTop: "6px",
                      background: "#fff", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                      width: "260px", zIndex: 10, overflow: "hidden",
                    }}
                  >
                    <div
                      onClick={() => { setRequestPickerOpen(false); navigate("/customer-issues"); }}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 16px", borderBottom: "1px solid #eee", cursor: "pointer",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>Complaints</span>
                      <span style={{ fontWeight: 700, color: "#2e7d32" }}>{openIssuesCount}</span>
                    </div>
                    <div
                      onClick={() => { setRequestPickerOpen(false); navigate("/customer-service-tickets"); }}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 16px", cursor: "pointer",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>Service Requests</span>
                      <span style={{ fontWeight: 700, color: "#2e7d32" }}>{openTicketsCount}</span>
                    </div>
                  </div>
                )}

              </div>

              <div className="activity-card">

                <h3>QUICK ACTIONS</h3>

                <div className="quick-actions-grid">
                  {QUICK_ACTIONS.map((action) => (
                    <button
                      key={action.path}
                      className="register-btn"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                      onClick={() => navigate(action.path)}
                    >
                      <action.icon size={16} color="white" />
                      {action.label}
                    </button>
                  ))}
                </div>

              </div>

            </>
          )}

        </div>

      </div>
    </>
  );
}

export default MyFarm;
